import React, { Suspense, useState } from 'react';

import { QuotaQueueInfo, quotaWaitSentence } from '../utils/quotaQueue';

import './QuotaQueueNotice.css';

// Loaded on demand: the billing modal pulls in the payment services, which neither the
// pages showing this notice nor their tests should load until someone asks to upgrade.
const UpgradeModal = React.lazy(() => import('./BillingModal').then((module) => ({ default: module.UpgradeModal })));

/** The press that starts the queued work again ("Send now", "Try again"): it checks the quota again. */
export interface QuotaQueueAction {
  label: string;
  onClick: () => void;
  busy?: boolean;
}

interface QuotaQueueNoticeProps {
  notice: QuotaQueueInfo;
  className?: string;
  source?: string;
  /** Nothing starts queued work again by itself: this is how the user does, once the quota has room. */
  action?: QuotaQueueAction;
}

const ActionButton: React.FC<{ action: QuotaQueueAction }> = ({ action }) => (
  <button type="button" className="quota-queue-notice__action" onClick={action.onClick} disabled={action.busy}
    aria-busy={action.busy}>
    <i className="fa-solid fa-rotate-right" aria-hidden="true" /> {action.label}
  </button>
);

export const QuotaQueueNotice: React.FC<QuotaQueueNoticeProps> = ({
  notice, className = '', action, source = 'quota:queue-notice',
}) => {
  const [upgrading, setUpgrading] = useState(false);

  return (
    <div className={`quota-queue-notice ${className}`.trim()} role="status" aria-label="Queued for plan quota">
      <i className="fa-solid fa-hourglass-half quota-queue-notice__icon" aria-hidden="true" />
      <div className="quota-queue-notice__body">
        <strong>{`You're in the queue for ${notice.plan_label} plan users' quota`}</strong>
        <p>{quotaWaitSentence(notice)}{notice.upgrade_available ? ' Upgrade now for faster access.' : ''}</p>
      </div>
      {action && <ActionButton action={action} />}
      {notice.upgrade_available && (
        <button type="button" className="quota-queue-notice__upgrade" onClick={() => setUpgrading(true)}>
          <i className="fa-solid fa-arrow-up" aria-hidden="true" /> Upgrade now
        </button>
      )}
      {upgrading && <Suspense fallback={null}><UpgradeModal source={source} onClose={() => setUpgrading(false)} /></Suspense>}
    </div>
  );
};

export type { QuotaQueueInfo };
